import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppHeader } from "@/components/AppHeader";
import { Pagination } from "@/components/Pagination";
import { fmtCurrency, fmtPct } from "@/lib/format";
import {
  Users,
  ChartLineUp,
  TrendUp,
  TrendDown,
  MagnifyingGlass,
  ArrowRight,
  CaretUp,
  CaretDown,
} from "@phosphor-icons/react";

const KpiCell = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="svx-grid-cell p-6 last:border-r-0">
    <div className="flex items-center gap-2 text-muted-foreground">
      <Icon size={16} weight="bold" className={accent ? "text-primary" : ""} />
      <span className="text-[10px] font-bold uppercase tracking-[0.2em]">{label}</span>
    </div>
    <div className="mt-3 font-display text-4xl font-black tracking-tighter text-foreground">
      {value}
    </div>
    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

const TrendBadge = ({ pct }) => {
  if (pct == null)
    return <span className="text-xs text-muted-foreground">—</span>;
  const up = pct >= 0;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold ${
        up ? "bg-emerald-500/10 text-emerald-600" : "bg-primary/10 text-primary"
      }`}
    >
      {up ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
      {fmtPct(pct)}
    </span>
  );
};

export default function DashboardPage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);

  const refreshData = useCallback(() => {
    axios.get(`${API}/dashboard/summary`).then((r) => setSummary(r.data)).catch(() => {});
    axios
      .get(`${API}/customers`, { params: query ? { q: query } : {} })
      .then((r) => setCustomers(r.data))
      .catch(() => {});
  }, [query]);

  useEffect(() => {
    axios.get(`${API}/dashboard/summary`).then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      axios
        .get(`${API}/customers`, { params: query ? { q: query } : {} })
        .then((r) => setCustomers(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => { setPage(1); }, [query, perPage]);

  const paged = customers.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="px-4 sm:px-6 py-8">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">
              {t.dashboard}
            </div>
            <h1 className="mt-1 font-display text-4xl font-black tracking-tighter text-foreground">
              {t.customers}
            </h1>
          </div>
        </div>

        {/* KPI Grid */}
        <div className="mb-10 grid grid-cols-1 border-t border-l border-border sm:grid-cols-2 lg:grid-cols-4">
          <KpiCell
            label={t.totalCustomers}
            value={summary ? summary.customer_count : "—"}
            icon={Users}
          />
          <KpiCell
            label={t.totalSalesYtd}
            value={summary ? fmtCurrency(summary.total_ytd_current, lang) : "—"}
            sub={
              summary && summary.total_change_pct != null
                ? `${fmtPct(summary.total_change_pct)} ${t.vsLastYear}`
                : null
            }
            icon={ChartLineUp}
            accent
          />
          <KpiCell
            label={t.growingCustomers}
            value={summary ? summary.growing_customers : "—"}
            icon={TrendUp}
          />
          <KpiCell
            label={t.decliningCustomers}
            value={summary ? summary.declining_customers : "—"}
            icon={TrendDown}
          />
        </div>

        {/* Search */}
        <div className="mb-4 flex items-center border border-border bg-card px-4">
          <MagnifyingGlass size={18} weight="bold" className="text-muted-foreground" />
          <input
            data-testid="customer-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.searchCustomers}
            className="w-full bg-transparent px-3 py-3 text-sm text-foreground outline-none placeholder-muted-foreground"
          />
        </div>

        <div className="mb-4 flex justify-end">
          <Pagination testid="dashboard-pagination-top" page={page} perPage={perPage} total={customers.length} onPage={setPage} onPerPage={setPerPage} />
        </div>

        {/* Customer table */}
        <div className="border border-border bg-card overflow-x-auto">
          <table data-testid="customers-table" className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b-2 border-border text-left">
                <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t.businessName}</th>
                <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t.accountNumber}</th>
                <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t.coopClientCode}</th>
                <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t.contact}</th>
                <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground text-right">{t.salesYtd}</th>
                <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground text-right">{t.trend}</th>
                <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground text-right">{t.products}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">{t.loading}</td></tr>
              )}
              {!loading && customers.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">{t.noResults}</td></tr>
              )}
              {!loading &&
                paged.map((c) => (
                  <tr
                    key={c.id}
                    data-testid={`customer-row-${c.id}`}
                    onClick={() => navigate(`/admin/customers/${c.id}`)}
                    className="cursor-pointer border-b border-border hover:bg-muted/50 transition-colors duration-200"
                  >
                    <td className="p-3 font-medium text-foreground">{c.business_name}</td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{c.account_number}</td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{c.coop_client_code || "—"}</td>
                    <td className="p-3 text-muted-foreground">{c.general_contact}</td>
                    <td className="p-3 text-right font-semibold text-foreground tabular-nums">{fmtCurrency(c.ytd_current, lang)}</td>
                    <td className="p-3 text-right"><TrendBadge pct={c.ytd_change_pct} /></td>
                    <td className="p-3 text-right tabular-nums text-muted-foreground">{c.product_count}</td>
                    <td className="p-3 text-right">
                      <ArrowRight size={16} weight="bold" className="inline text-muted-foreground" />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-end">
          <Pagination testid="dashboard-pagination-bottom" page={page} perPage={perPage} total={customers.length} onPage={setPage} onPerPage={setPerPage} />
        </div>
      </main>
    </div>
  );
}
